type Point = { x: number; y: number };

export interface SpatialLandmark extends Point {
  z?: number;
  visibility?: number;
}

/** Minimal captured-view shape that carries pixel-space overlay data. */
export interface SpatialCapturedFields {
  landmarks?: SpatialLandmark[] | null;
  cmPerPx?: number;
  custom?: Partial<Record<string, Point | undefined>>;
  floorCorrectionPivot?: Point | null;
  manualFloorPoints?: [Point, Point];
  plLandmarks?: {
    control?: Point;
  };
}

function scalePoint<T extends Point>(point: T, scale: number): T {
  return { ...point, x: point.x * scale, y: point.y * scale };
}

function scaleCustomLandmarks(
  custom: SpatialCapturedFields['custom'],
  scale: number,
): SpatialCapturedFields['custom'] {
  if (!custom) return custom;
  const out: NonNullable<SpatialCapturedFields['custom']> = {};
  for (const [key, value] of Object.entries(custom)) {
    if (value) out[key] = scalePoint(value, scale);
  }
  return out;
}

/**
 * Scale pixel-space fields on a captured view after the image was resized.
 * `scale` is newSize / originalSize (uniform; aspect ratio preserved).
 */
export function scaleCapturedSpatialData<T extends SpatialCapturedFields>(
  entry: T,
  scale: number,
): T {
  if (!Number.isFinite(scale) || scale <= 0 || scale === 1) return entry;

  const landmarks = Array.isArray(entry.landmarks)
    ? entry.landmarks.map((lm) => (lm ? scalePoint(lm, scale) : lm))
    : entry.landmarks;

  const cmPerPx =
    typeof entry.cmPerPx === 'number' && Number.isFinite(entry.cmPerPx)
      ? entry.cmPerPx / scale
      : entry.cmPerPx;

  return {
    ...entry,
    landmarks,
    cmPerPx,
    custom: scaleCustomLandmarks(entry.custom, scale),
    floorCorrectionPivot: entry.floorCorrectionPivot
      ? scalePoint(entry.floorCorrectionPivot, scale)
      : entry.floorCorrectionPivot,
    manualFloorPoints: entry.manualFloorPoints
      ? [
          scalePoint(entry.manualFloorPoints[0], scale),
          scalePoint(entry.manualFloorPoints[1], scale),
        ]
      : entry.manualFloorPoints,
    plLandmarks: entry.plLandmarks?.control
      ? { ...entry.plLandmarks, control: scalePoint(entry.plLandmarks.control, scale) }
      : entry.plLandmarks,
  };
}

/**
 * True when landmarks appear to share the same pixel space as the displayed image.
 * Used to detect historical prospect docs where images were compressed but landmarks were not scaled.
 */
export function landmarksFitImage(
  landmarks: SpatialLandmark[] | null | undefined,
  imageWidth: number,
  imageHeight: number,
  marginRatio = 0.08,
): boolean {
  if (!landmarks?.length || imageWidth <= 0 || imageHeight <= 0) return false;

  const pts = landmarks.filter(
    (p): p is SpatialLandmark => !!p && Number.isFinite(p.x) && Number.isFinite(p.y),
  );
  if (pts.length === 0) return false;

  const maxX = Math.max(...pts.map((p) => p.x));
  const maxY = Math.max(...pts.map((p) => p.y));
  const minX = Math.min(...pts.map((p) => p.x));
  const minY = Math.min(...pts.map((p) => p.y));

  const maxDim = Math.max(imageWidth, imageHeight);
  const margin = maxDim * marginRatio;

  // Completely off-canvas (typical when landmarks are from a much larger original)
  if (maxX > imageWidth + margin || maxY > imageHeight + margin) return false;
  if (minX < -margin || minY < -margin) return false;

  // Body should span a meaningful portion of the frame; tiny coords on a large image also mismatch
  const spanX = maxX - minX;
  const spanY = maxY - minY;
  if (spanY < imageHeight * 0.15 && maxY < imageHeight * 0.4) return false;
  if (spanX < 1 && spanY < 1) return false;

  return true;
}
